import type { NodeMock, StatePreset } from "../mock-types";

export const captainDraftMock: NodeMock = {
  label: "Captain Draft",
  description: "Captains take turns picking players",
  state: (players, teamCount) => {
    // Captains as string[] (index = team-1), teams as Record<string, number>
    const captains: string[] = [];
    const teams: Record<string, number> = {};
    for (let t = 0; t < teamCount; t++) {
      const captainId = players[t]?.userId ?? `captain_${t}`;
      captains.push(captainId);
      teams[captainId] = t + 1; // 1-indexed
    }
    const pickPool = players.slice(teamCount).map((p) => p.userId);
    const playerMap = new Map(players.map((p) => [p.userId, p]));

    // Auto-draft: if a captain is in a party, pull their party members out of
    // the pool and onto the captain's team automatically.
    const autoDraftedPlayers: string[] = [];
    for (let t = 0; t < teamCount; t++) {
      const cpt = playerMap.get(captains[t]);
      if (!cpt?.partyId) continue;
      const partyInPool = pickPool.filter((id) => {
        const p = playerMap.get(id);
        return p?.partyId === cpt.partyId;
      });
      for (const memberId of partyInPool) {
        const idx = pickPool.indexOf(memberId);
        if (idx !== -1) {
          pickPool.splice(idx, 1);
          teams[memberId] = t + 1;
          autoDraftedPlayers.push(memberId);
        }
      }
    }

    // First pick goes to the team with the fewest members (fairness after auto-draft)
    const teamSizes = Array.from({ length: teamCount }, () => 0);
    for (const team of Object.values(teams)) {
      teamSizes[team - 1]++;
    }
    const minSize = Math.min(...teamSizes);
    let firstPicker = 1;
    for (let t = 0; t < teamCount; t++) {
      if (teamSizes[t] === minSize) {
        firstPicker = t + 1;
        break;
      }
    }

    return {
      captains,
      teams,
      pickPool,
      autoDraftedPlayers,
      currentPickingTeam: firstPicker,
      pickIndex: autoDraftedPlayers.length,
      totalPicks: pickPool.length + autoDraftedPlayers.length,
      phase: "drafting" as const,
    };
  },
  config: {
    timeoutSecondsPerPick: 30,
    captainSelection: "highest_elo",
    draftOrder: "snake",
    labels: {
      title: "Captain Draft",
    },
  },
};

export const captainDraftPresets: StatePreset[] = [
  {
    label: "Fresh",
    build: ({ defaultState }) => ({
      state: { ...defaultState, pickIndex: 0, currentPickingTeam: 1 },
    }),
  },
  {
    label: "Your Pick",
    build: ({ players, viewerId, defaultState, teamCount }) => {
      const perTeam = Math.ceil(players.length / teamCount);
      const viewerIdx = players.findIndex((p) => p.userId === viewerId);
      const viewerTeam =
        viewerIdx < teamCount
          ? viewerIdx + 1
          : Math.min(Math.floor(viewerIdx / perTeam), teamCount - 1) + 1;
      return { state: { ...defaultState, currentPickingTeam: viewerTeam } };
    },
  },
  {
    label: "Complete",
    build: ({ players, teamCount, defaultState }) => {
      const teams: Record<string, number> = {};
      const perTeam = Math.ceil(players.length / teamCount);
      players.forEach((p, i) => {
        teams[p.userId] = Math.min(Math.floor(i / perTeam), teamCount - 1) + 1;
      });
      return {
        state: {
          ...defaultState,
          teams,
          pickPool: [],
          phase: "complete",
          pickIndex: players.length,
        },
      };
    },
  },
];
